/**
 * NL-001 / NL-030 (2026-06-09) — shared accessor for the signed-in
 * client's `current_step`. Powers route guards (StepGate), the locked
 * nav variant in AppLayout, the dashboard WeeklyFocus locked card, and
 * the existing per-card step gates on /check-in.
 *
 * Implementation notes:
 *
 * - For non-client users (coach / head_coach) `step` is `null` — they
 *   bypass StepGate entirely (no per-coach locking).
 * - The hook fetches `getClientSelf` once per (clientId, accessToken)
 *   pair via a process-wide promise cache, so multiple consumers
 *   sharing the same render don't fan out duplicate requests.
 * - The cache busts on `clientId` change; coach apps switching
 *   between clients via `?clientId=` aren't affected because they
 *   pass through `useClientId` directly, not through this hook.
 * - Refresh is opt-in via `useClientStep().refresh()`. Step advances
 *   are infrequent (coach action) and we don't want to spam the API
 *   on every focus change.
 */
import { useEffect, useState, useCallback } from 'react';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';

interface CacheEntry {
  promise: Promise<api.ClientSelf>;
  value?: api.ClientSelf;
  error?: unknown;
}

const cache = new Map<string, CacheEntry>();

function loadSelf(accessToken: string, clientId: string): Promise<api.ClientSelf> {
  const key = `${clientId}::${accessToken}`;
  const hit = cache.get(key);
  if (hit) return hit.promise;
  const promise = api
    .getClientSelf(accessToken, clientId)
    .then((self) => {
      const cached = cache.get(key);
      if (cached) cached.value = self;
      return self;
    })
    .catch((err: unknown) => {
      const cached = cache.get(key);
      if (cached) cached.error = err;
      // Drop on failure so the next mount retries.
      cache.delete(key);
      throw err;
    });
  cache.set(key, { promise });
  return promise;
}

export interface UseClientStep {
  /** `null` for non-clients, while loading, and when the fetch fails. */
  step: number | null;
  /** True until the first response (success or failure) settles. */
  loading: boolean;
  /** True iff the signed-in user is a client (has role:'client'). Drives
   *  the gate logic so coaches always bypass and clients with a failed
   *  fetch default conservatively to locked. */
  isClient: boolean;
  /** Drop the cache for the current (clientId, token) pair. */
  refresh: () => void;
}

export function useClientStep(): UseClientStep {
  const { user, accessToken } = useAuth();
  const isClient = user?.role === 'client';
  const clientId = isClient ? user.clientId ?? null : null;
  const [step, setStep] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(isClient);

  useEffect(() => {
    if (!clientId) {
      setStep(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    void loadSelf(accessToken ?? '', clientId)
      .then((self) => {
        if (active) setStep(self.current_step);
      })
      .catch(() => {
        if (active) setStep(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const refresh = useCallback(() => {
    if (!clientId) return;
    cache.delete(`${clientId}::${accessToken ?? ''}`);
    setLoading(true);
    void loadSelf(accessToken ?? '', clientId)
      .then((self) => setStep(self.current_step))
      .catch(() => setStep(null))
      .finally(() => setLoading(false));
  }, [accessToken, clientId]);

  return { step, loading, isClient, refresh };
}

/**
 * Test-only — drop every cached entry. Called from
 * `apps/web/test/setup.ts` between specs so seeded localStorage
 * sessions don't leak across describe blocks.
 */
export function __clearClientStepCacheForTests(): void {
  cache.clear();
}
